// إدارة مواسم Call of Duty Mobile.
import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "../contexts/AdminAuthContext";
import { adminApi, formatApiError } from "../lib/api";
import AdminHeader from "../components/AdminHeader";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  Save,
  Calendar,
  RefreshCw,
  Trophy,
  Crosshair,
  Target,
} from "lucide-react";

const ICON_OPTIONS = [
  { value: "trophy", label: "🏆 تصنيف" },
  { value: "crosshair", label: "🎯 DMZ" },
  { value: "target", label: "🎪 باتل باس" },
];

const ACCENT_OPTIONS = [
  { value: "gold", label: "ذهبي" },
  { value: "red", label: "أحمر" },
  { value: "purple", label: "بنفسجي" },
];

const isoToDate = (iso) => (iso ? iso.slice(0, 10) : "");
const dateToIso = (v) => (v ? `${v}T00:00:00Z` : "");

const emptySeason = {
  id: "",
  title: "",
  seasonLabel: "S1",
  icon: "trophy",
  accent: "gold",
  startAt: "",
  endAt: "",
  startDateLabel: "",
  endDateLabel: "",
};

const SeasonForm = ({ open, initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(emptySeason);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptySeason, ...initial } : { ...emptySeason });
      setError("");
    }
  }, [open, initial]);

  if (!open) return null;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim()) return setError("عنوان الموسم مطلوب.");
    if (!form.seasonLabel.trim()) return setError("رمز الموسم مطلوب (مثل S7).");
    if (!form.startAt || !form.endAt) return setError("تاريخا البداية والنهاية مطلوبان.");

    const payload = {
      title: form.title.trim(),
      seasonLabel: form.seasonLabel.trim(),
      icon: form.icon,
      accent: form.accent,
      startAt: form.startAt,
      endAt: form.endAt,
      startDateLabel: form.startDateLabel.trim(),
      endDateLabel: form.endDateLabel.trim(),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.patch(`/admin/seasons/${initial.id}`, payload);
      } else {
        const withId = { ...payload };
        if (form.id.trim()) withId.id = form.id.trim();
        await adminApi.post(`/admin/seasons`, withId);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" dir="rtl">
      <form
        onSubmit={handleSubmit}
        data-testid="season-form"
        className="w-full max-w-lg my-6 rounded-2xl bg-gradient-to-b from-[#0F1D42] to-[#0A1128] border border-[#C9A96A]/20 shadow-2xl"
      >
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/5">
          <h3 className="text-white text-lg font-black">
            {isEdit ? `تعديل: ${initial.title}` : "إضافة موسم جديد"}
          </h3>
          <button
            type="button"
            data-testid="season-form-close"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-3">
          <Field label="اسم الموسم" required>
            <input
              type="text"
              dir="auto"
              data-testid="season-title"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
              placeholder="موسم التصنيف"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="رمز الموسم" required>
              <input
                type="text"
                dir="ltr"
                data-testid="season-label"
                value={form.seasonLabel}
                onChange={(e) => setField("seasonLabel", e.target.value)}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
                placeholder="S7"
              />
            </Field>
            <Field label="لون الموسم">
              <select
                data-testid="season-accent"
                value={form.accent}
                onChange={(e) => setField("accent", e.target.value)}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
              >
                {ACCENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-[#0A1128]">
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="أيقونة">
              <select
                data-testid="season-icon"
                value={form.icon}
                onChange={(e) => setField("icon", e.target.value)}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
              >
                {ICON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-[#0A1128]">
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="تاريخ البداية (3ص السعودية)" required>
              <input
                type="date"
                data-testid="season-startAt"
                value={isoToDate(form.startAt)}
                onChange={(e) => setField("startAt", dateToIso(e.target.value))}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
              />
            </Field>
            <Field label="تاريخ النهاية (3ص السعودية)" required>
              <input
                type="date"
                data-testid="season-endAt"
                value={isoToDate(form.endAt)}
                onChange={(e) => setField("endAt", dateToIso(e.target.value))}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
              />
            </Field>
            <Field label="تسمية البداية (عربي)">
              <input
                type="text"
                dir="auto"
                data-testid="season-startDateLabel"
                value={form.startDateLabel}
                onChange={(e) => setField("startDateLabel", e.target.value)}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
                placeholder="6 يوليو"
              />
            </Field>
            <Field label="تسمية النهاية (عربي)">
              <input
                type="text"
                dir="auto"
                data-testid="season-endDateLabel"
                value={form.endDateLabel}
                onChange={(e) => setField("endDateLabel", e.target.value)}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
                placeholder="9 سبتمبر"
              />
            </Field>
          </div>

          {!isEdit && (
            <Field label="معرّف مخصّص (اختياري)">
              <input
                type="text"
                dir="ltr"
                data-testid="season-id"
                value={form.id}
                onChange={(e) => setField("id", e.target.value)}
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
                placeholder="battlepass_s7"
              />
            </Field>
          )}

          {error && (
            <div data-testid="season-form-error" className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 sm:p-5 border-t border-white/5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-white/[0.04] border border-white/10 text-white/70 text-sm font-bold transition"
          >
            إلغاء
          </button>
          <button
            type="submit"
            data-testid="season-form-save"
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] font-black text-sm hover:brightness-110 transition disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "حفظ" : "إنشاء"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-white/70 text-[11px] font-bold mb-1 block">
      {label} {required && <span className="text-red-300">*</span>}
    </label>
    {children}
  </div>
);

const ICON_COMP = { trophy: Trophy, crosshair: Crosshair, target: Target };

const SeasonRow = ({ season, onEdit, onDelete }) => {
  const [confirm, setConfirm] = useState(false);
  const Icon = ICON_COMP[season.icon] || Trophy;
  return (
    <div
      data-testid={`admin-season-row-${season.id}`}
      className="rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:items-center"
    >
      <div className="w-12 h-12 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-white font-black text-base sm:text-lg">{season.title}</h3>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C9A96A]/15 border border-[#C9A96A]/30 text-[#E5CB94]">
            {season.seasonLabel}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-white/50 text-[11px]">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span dir="ltr" className="tabular-nums">{isoToDate(season.startAt)}</span> → <span dir="ltr" className="tabular-nums">{isoToDate(season.endAt)}</span>
          </span>
        </div>
      </div>
      <div className="flex sm:flex-col gap-2">
        <button
          type="button"
          data-testid={`admin-season-edit-${season.id}`}
          onClick={() => onEdit(season)}
          className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-[#C9A96A]/15 hover:bg-[#C9A96A]/25 border border-[#C9A96A]/40 text-[#E5CB94] text-[11px] font-bold transition flex-1"
        >
          <Pencil className="w-3.5 h-3.5" />
          تعديل
        </button>
        {confirm ? (
          <div className="flex gap-1">
            <button
              type="button"
              data-testid={`admin-season-delete-confirm-${season.id}`}
              onClick={() => { setConfirm(false); onDelete(season); }}
              className="inline-flex items-center gap-1 h-9 px-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[10px] font-bold transition"
            >
              <Trash2 className="w-3 h-3" />
              تأكيد
            </button>
            <button
              type="button"
              onClick={() => setConfirm(false)}
              className="inline-flex items-center h-9 px-2 rounded-lg bg-white/[0.04] border border-white/10 text-white/70 text-[10px] font-bold transition"
            >
              إلغاء
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`admin-season-delete-${season.id}`}
            onClick={() => setConfirm(true)}
            className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-red-500/15 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-200 text-[11px] font-bold transition"
          >
            <Trash2 className="w-3.5 h-3.5" />
            حذف
          </button>
        )}
      </div>
    </div>
  );
};

const AdminSeasonsPage = () => {
  const { isAuthenticated, loading: authLoading } = useAdminAuth();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);

  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/seasons");
      setSeasons(data?.items || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchSeasons();
  }, [isAuthenticated, fetchSeasons]);

  const handleDelete = async (season) => {
    setError("");
    try {
      await adminApi.delete(`/admin/seasons/${season.id}`);
      setSeasons((prev) => prev.filter((s) => s.id !== season.id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A1128] flex items-center justify-center text-white/50 text-sm">
        جارٍ التحقّق من الجلسة...
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-[#0A1128] text-white" dir="rtl">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04] z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative z-10">
        <AdminHeader title="إدارة المواسم" />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5">
          <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-white/80 text-sm">
              <span className="text-[#C9A96A] font-bold">{seasons.length}</span> موسم مسجّل
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="admin-seasons-refresh"
                onClick={fetchSeasons}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 text-[11px] font-bold transition"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                تحديث
              </button>
              <button
                type="button"
                data-testid="admin-seasons-create"
                onClick={() => { setFormInitial(null); setFormOpen(true); }}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] text-[11px] font-black transition hover:brightness-110"
              >
                <Plus className="w-4 h-4" />
                موسم جديد
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="admin-seasons-error" className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3">
              {error}
            </div>
          )}

          {loading && seasons.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-slate-400 text-center py-16 text-sm inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-4 h-4 animate-spin" />
              جارٍ التحميل...
            </div>
          ) : seasons.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-center py-16 text-sm text-white/60">
              لا توجد مواسم — أضف أول موسم.
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {seasons.map((s) => (
                <SeasonRow
                  key={s.id}
                  season={s}
                  onEdit={(sn) => { setFormInitial(sn); setFormOpen(true); }}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      <SeasonForm
        open={formOpen}
        initial={formInitial}
        onClose={() => setFormOpen(false)}
        onSaved={fetchSeasons}
      />
    </div>
  );
};

export default AdminSeasonsPage;
